import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Permissions } from '../../config';
import useSWR from 'swr';

// material-ui
import {
    Assignment,
    AddAlert,
    Visibility,
    VisibilityOff,
    Add,
    ViewModule,
    ViewList,
} from '@mui/icons-material';
import { createUseStyles } from 'react-jss';

// core components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaFilter from 'components/SparaFilter/SparaFilter';

import stylesAlert from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js';
import styles from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js';

import { fetcher, groupApi } from '../../lib/api';
import GroupCard from 'components/GroupCard/GroupCard';
import GroupCardSkeleton from 'components/GroupCardSkeleton';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import GroupList from 'components/GroupList/GroupList';
import Roles from 'config/Roles';

const style = {
    ...styles,
    ...stylesAlert,
    cardsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '12px',
        padding: '12px',
    },
    noResults: {
        textAlign: 'center',
        padding: '32px',
        color: 'rgba(0,0,0,0.5)',
        fontSize: '16px',
    },
};

const useStyles = createUseStyles(style);

const Group = ({ t }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
    const [filters, setFilters] = useState({
        visibility: searchParams.get('visibility')
            ? searchParams.get('visibility') === 'true'
            : null,
        type: searchParams.get('type') || null,
        hasMembershipCard: searchParams.get('hasMembershipCard')
            ? searchParams.get('hasMembershipCard') === 'true'
            : null,
    });
    const userPermission = JSON.parse(localStorage.getItem('user')).permission;
    const userRoles = JSON.parse(localStorage.getItem('user')).roles;
    const canCreate = userPermission.includes(Permissions.GROUP_CREATE);
    const canUpdate = userPermission.includes(Permissions.GROUP_UPDATE);
    const isGroupAdmin = userPermission.includes(Permissions.CU_GROUP_UPDATE);
    const [search, setSearch] = useState(searchParams.get('search') || '');

    const { data: specialCompanies, error, isLoading } = useSWR(
        groupApi.list({
            'my-groups': isGroupAdmin ? true : null,
        }),
        fetcher
    );

    useEffect(() => {
        if (specialCompanies?.results?.length === 1) {
            navigate(`/group/${specialCompanies.results[0].id}`);
        }
    }, [specialCompanies, navigate]);

    const handleCardClick = (special_company_data) => {
        navigate(`/group/${special_company_data.id}`);
    };

    const handleFilterClick = (filterType, value) => {
        const newFilters = {
            ...filters,
            [filterType]: filters[filterType] === value ? null : value,
        };
        setFilters(newFilters);

        // Update URL parameters
        const params = new URLSearchParams(searchParams);
        if (newFilters[filterType] === null) {
            params.delete(filterType);
        } else {
            params.set(filterType, newFilters[filterType]);
        }
        setSearchParams(params);
    };

    const handleSearchChange = (event) => {
        const newSearch = event.target.value || '';
        setSearch(newSearch);

        // Update URL parameters
        const params = new URLSearchParams(searchParams);
        if (newSearch) {
            params.set('search', newSearch);
        } else {
            params.delete('search');
        }
        setSearchParams(params);
    };

    const handleClearAll = () => {
        // Clear all filters
        setFilters({
            visibility: null,
            type: null,
            hasMembershipCard: null,
        });

        // Clear URL parameters
        const params = new URLSearchParams(searchParams);
        params.delete('visibility');
        params.delete('type');
        params.delete('hasMembershipCard');
        setSearchParams(params);
    };

    const filteredGroups = useMemo(() => {
        if (!specialCompanies?.results) return [];

        return specialCompanies.results.filter((group) => {
            // Apply existing filters
            if (filters.visibility !== null && group.hidden !== filters.visibility) return false;
            if (filters.type !== null && group.type !== filters.type) return false;
            if (
                filters.hasMembershipCard !== null &&
                group.has_membership_card !== filters.hasMembershipCard
            )
                return false;

            // Apply search filter
            if (search) {
                const searchLower = search.toLowerCase();
                return (
                    group.name.toLowerCase().includes(searchLower) ||
                    (group.description && group.description.toLowerCase().includes(searchLower))
                );
            }

            return true;
        });
    }, [specialCompanies?.results, filters, search]);

    const filterGroups = [
        // Sýnileiki hópur
        [
            {
                id: 'visibility',
                label: 'visible',
                value: false,
                icon: Visibility,
                color: '#4caf50',
            },
            {
                id: 'visibility',
                label: 'hidden',
                value: true,
                icon: VisibilityOff,
                color: '#f44336',
            },
        ],
        // Tegund hópur
        [
            {
                id: 'type',
                label: 'group_type_regular',
                value: 'regular',
                color: '#2196f3',
            },
            {
                id: 'type',
                label: 'group_type_lite',
                value: 'lite',
                color: '#4caf50',
            },
            {
                id: 'type',
                label: 'group_type_golden',
                value: 'golden',
                color: '#ffc107',
            },
        ],
        // Membership card hópur
        [
            {
                id: 'hasMembershipCard',
                label: 'has_membership_card',
                value: true,
                color: '#2196f3',
            },
            {
                id: 'hasMembershipCard',
                label: 'no_membership_card',
                value: false,
                color: '#f44336',
            },
        ],
    ];

    if (error) return <div>{t('common.error_loading_data')}</div>;

    return (
        <GridContainer>
            <GridItem xs={12}>
                <SparaTitle
                    title={t('menu.groups')}
                    rightComponent={
                        <>
                            <Button
                                color={viewMode === 'cards' ? 'primary' : 'default'}
                                onClick={() => setViewMode('cards')}
                                style={{ marginRight: '8px' }}
                            >
                                <ViewModule />
                            </Button>
                            <Button
                                color={viewMode === 'list' ? 'primary' : 'default'}
                                onClick={() => setViewMode('list')}
                                style={{ marginRight: '8px' }}
                            >
                                <ViewList />
                            </Button>
                            {canCreate && (
                                <SparaButton
                                    color="primary"
                                    icon={Add}
                                    onClick={() => navigate('/group/new')}
                                >
                                    Nýr hópur
                                </SparaButton>
                            )}
                        </>
                    }
                />
                <Card style={{ margin: '0px' }}>
                    <CardBody>
                        {/* If is SUPER_ADMIN */}
                        {userRoles.includes(Roles.SUPER_ADMIN) && (
                            <SparaFilter
                                filters={filters}
                                onFilterClick={handleFilterClick}
                                onClearAll={handleClearAll}
                                search={search}
                                onSearchChange={handleSearchChange}
                                filterGroups={filterGroups}
                                t={t}
                            />
                        )}
                        {isLoading ? (
                            <div className={classes.cardsContainer}>
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <GroupCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : filteredGroups.length === 0 ? (
                            <div className={classes.noResults}>
                                {t('no_results_with_current_filter')}
                            </div>
                        ) : viewMode === 'cards' ? (
                            <div className={classes.cardsContainer}>
                                {filteredGroups.map((group) => (
                                    <GroupCard
                                        key={group.id}
                                        group={group}
                                        onClick={handleCardClick}
                                        t={t}
                                    />
                                ))}
                            </div>
                        ) : (
                            <GroupList groups={filteredGroups} onClick={handleCardClick} t={t} />
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default withTranslation('', { withRef: true })(Group);
